import styles from './sidebar.module.css';
import type { SidebarNavItem } from './sidebar.types';
import { navigateToUrl } from 'single-spa';
import leafSvg from '../icons/leaf.svg?raw';
import shieldCheckSvg from '../icons/shield-check.svg?raw';
import partstraderLogoSvg from '../icons/partstrader-light.svg?raw';

const defaultItems: SidebarNavItem[] = [
    {
        id: 'home',
        label: 'Home',
        href: '/',
        icon: leafSvg,
    },
    {
        id: 'settings',
        label: 'Settings',
        href: '/settings',
        icon: shieldCheckSvg,
    },
];

class Sidebar extends HTMLElement {
    private items: SidebarNavItem[] = defaultItems;
    private activeRoute: string = '';
    private footer: string | null = null;

    connectedCallback(): void {
        // Get current path
        this.activeRoute = window.location.pathname;

        this.render();
        this.attachEventListeners();

        // Listen for route changes
        window.addEventListener('popstate', () => {
            this.activeRoute = window.location.pathname;
            this.render();
        });
    }

    render(): void {
        this.innerHTML = `
            <aside class="${styles.sidebar}">
                <a class="${styles.logoLink}" data-testid="sidebar-logo-link">
                    ${partstraderLogoSvg}
                </a>
                <nav class="${styles.sidebarNav}" role="navigation">
                    <ul class="${styles.navList}">
                        ${this.items.length > 0 ? this.renderItems() : ''}
                    </ul>
                    ${this.footer ? `<footer class="${styles.sidebarFooter}">${this.footer}</footer>` : ''}
                </nav>
            </aside>
        `;
    }

    renderItems(): string {
        return this.items
            .map(
                (item) => `
                <li class="${styles.navItem}">
                    <a
                        class="${styles.navLink} ${item.href === this.activeRoute ? styles.active : ''}"
                        data-testid="sidebar-item-${item.id}"
                        ${item.href ? `href="${item.href}"` : ''}
                        data-id="${item.id}"
                    >
                        ${item.icon ? `<span class="${styles.navIcon}">${item.icon}</span>` : ''}
                        <span class="${styles.navText}">${item.label}</span>
                    </a>
                </li>
            `,
            )
            .join('');
    }

    navigateTo(path: string) {
        // Use single-spa's navigation
        navigateToUrl(path);
        this.activeRoute = path;
        this.render();
        this.attachEventListeners();
    }

    attachEventListeners(): void {
        const logoLink = this.querySelector(`.${styles.logoLink}`);
        if (logoLink) {
            logoLink.addEventListener('click', (e) => {
                e.preventDefault();
                navigateToUrl('/');
                this.activeRoute = '/';
                this.render();
                this.attachEventListeners();
            });
        }

        // Handle nav item clicks
        const navLinks = this.querySelectorAll(`.${styles.navLink}`);
        navLinks.forEach((link) => {
            link.addEventListener('click', (e) => {
                e.preventDefault();

                const itemId = (link as HTMLElement).dataset.id;
                const item = this.items.find((i) => i.id === itemId);

                // Navigate using single-spa if href is provided
                if (item?.href) {
                    navigateToUrl(item.href);
                    this.activeRoute = item.href;
                    this.render();
                    this.attachEventListeners();
                }
            });
        });

        // Update active state based on current path
        this.updateActiveLink();

        // Listen for navigation changes
        window.addEventListener('popstate', () => {
            this.updateActiveLink();
        });
    }

    updateActiveLink(): void {
        const currentPath = window.location.pathname;
        const links = this.querySelectorAll(`.${styles.navLink}`);

        links.forEach((link) => {
            const href = link.getAttribute('href');
            if (href === currentPath) {
                link.classList.add(styles.active);
            } else {
                link.classList.remove(styles.active);
            }
        });
    }
}

// Register the custom element
customElements.define('sidebar-component', Sidebar);

export { Sidebar };
